
import Vue from 'vue';
import { getEnv } from '@root/common/utils/getEnv';
import rootConfig from '@root/config/root.config';
import { Config, DataComments } from '@root/modules/article/types/article';
import ArticleConfig from '@root/modules/article/config/article.config';
import ArticleForCommentsService from '@root/modules/article/services/ArticleForComments.service';
import AdsLayout from '@root/common/components/layout/AdsLayout.vue';
import LinkHandler from '@core/components/helpers/LinkHandler.vue';
import { loadScripts } from '@root/common/utils/scriptLoader';
import { AdFormOptions } from '@ads/types/AdForm';
import CommentsSkeletonLoader from '@root/modules/article/components/CommentsSkeletonLoader.vue';
import PianoCustomerStateHandler from '@piano/components/PianoCustomerStateHandler.vue';

import { getArticleMeta } from '@root/modules/article/utils/articleMeta';
import getArticleAdsSettings from '@root/modules/article/utils/getArticleAdsSettings';

import { gridConfig } from '@core/config/grid.config';

interface Data {
  article: DataComments | null;
  commentsAds: Config['commentsAds'];
  showComments: boolean;
  isClient: boolean;
  articleUrl: string;
  isMobileView: boolean;
  loadCommentsOnMount: boolean;
}

interface Computed {
  commentsContainer: string;
  adsPlacements: Record<string, AdFormOptions> | null;
  adsAttributes: {
    mkv: Record<string, string>;
    mkw: string[];
  };
  disabledAdsList: string[];
  disableComments: boolean;
  title: string;
  hasChannelAccess: boolean;
  hideOrdering: boolean;
}

interface Methods {
  loadComments: () => void;
  loadCommentsScript: () => void;
  loadCommentsEmbed: () => void;
  openLoginFormModal: () => void;
}

interface CommentsWindow extends Window {
  showCommentDialogLoaded: boolean;
  loginApiToken: string;
  showCommentDialog: (id: string, settings: Record<string, any>) => void;
  _dlf: any;
}

declare let window: CommentsWindow;

export default Vue.extend<Data, Methods, Computed, unknown>({
  components: {
    AdsLayout,
    LinkHandler,
    CommentsSkeletonLoader,
    PianoCustomerStateHandler,
  },
  validate({ params }) {
    return /^\d+$/.test(params.id) && /^[a-z0-9-]+$/.test(params.slug);
  },
  async fetch() {
    const articleId = this.$route.params.id;
    const { lang } = this.$channelConfig('settings');

    try {
      const variables = {
        id: articleId,
        authorLanguage: lang,
      };

      const articleForCommentsService = new ArticleForCommentsService({ req: this.$ssrContext?.req });
      const data = await articleForCommentsService.fetch(variables);

      if (!data?.article) {
        this.$nuxt.error({ statusCode: 404, message: 'error.page_not_found' });
        return;
      }

      this.$store.commit('article/setActiveArticle', data.article);

      this.$store.commit(
        'adForm/setPageAdsSettings',
        getArticleAdsSettings({ $channelConfig: this.$channelConfig, $route: this.$route, article: data.article })
      );

      this.article = data.article;
      this.isClient = process.client;

      this.articleUrl = `${this.$channelConfig('meta').base.baseUrl}/a/${articleId}`;

      if (this.$isServer) {
        this.loadCommentsOnMount = true;
      } else {
        await this.loadComments();
      }
    } catch (e) {
      this.$sentry.captureException(`Fetch article for comments failed: ${e}`, { tags: { 'process.server': process.server ? 'server' : 'client' } });
      this.$nuxt.error(e);
    }
  },
  data() {
    return {
      article: null,
      commentsAds: ArticleConfig.commentsAds,
      showComments: false,
      isClient: process.client,
      articleUrl: '',
      isMobileView: false,
      loadCommentsOnMount: false,
    };
  },
  computed: {
    commentsContainer() {
      return `comments-fe-${this.$route.params.id}`;
    },
    adsPlacements() {
      return this.$store.getters['adForm/getAdsPlacements'];
    },
    adsAttributes() {
      const keywords: string[] = this.$store.getters['adForm/getAdsKeywords'];
      const keyValues: Record<string, string> = this.$store.getters['adForm/getAdsKeyValues'];

      return {
        mkw: keywords,
        mkv: keyValues,
      };
    },
    disabledAdsList() {
      return this.$store.getters['adForm/getDisabledAdsList'];
    },
    disableComments() {
      return rootConfig.disableComments;
    },
    title() {
      if (!this.article) {
        return '';
      }

      return this.article.content.title.html.replace(/(<\/?h1>)/gi, '');
    },
    hasChannelAccess() {
      return this.$store.state.piano.access.channelAccess;
    },
    hideOrdering() {
      const { specialFeatures } = this.$channelConfig('settings').application;
      return specialFeatures.hideSignupAndOrders.enabled;
    },
  },
  async mounted() {
    if (process.client) {
      const screenWidth = window.screen.width;
      const isIpad = (/Macintosh/i.test(navigator.userAgent) && navigator.maxTouchPoints && navigator.maxTouchPoints > 1) as boolean;
      this.isMobileView = screenWidth <= gridConfig.breakpoints.lg || isIpad;
    } else {
      this.isMobileView = this.$ua && (this.$ua.isFromSmartphone() || this.$ua.isFromMobilephone() || this.$ua.isFromTablet());
    }

    window.addEventListener('commentFeInited', () => {
      this.showComments = true;
    });

    if (this.loadCommentsOnMount) {
      await this.loadComments();
    }
  },
  beforeDestroy() {
    this.$store.commit('article/setActiveArticle', null);
  },
  beforeRouteLeave(to, from, next) {
    this.$store.commit('adForm/resetPageAdsSettings');
    next();
  },
  methods: {
    async loadComments() {
      // Do not load comments script if global disableComments flag is set
      if (rootConfig.disableComments) {
        return;
      }

      await this.$nextTick();

      if (this.$store.state.piano.isScriptInited) {
        this.loadCommentsScript();
      } else if (!this.$store.state.piano.isScriptInited) {
        this.$store.watch(
          (state) => state.piano.isScriptInited,
          async () => {
            this.loadCommentsScript();
          }
        );
      }
    },
    loadCommentsScript() {
      // Do not load comment-fe script if it is already loaded
      if (!window.showCommentDialog) {
        loadScripts(getEnv('commentFeUrl'))
          .then(() => {
            this.loadCommentsEmbed();
          })
          .catch((e) => console.error('comments loadScript error', e));
      } else {
        this.loadCommentsEmbed();
      }
    },
    loadCommentsEmbed() {
      if (!this.$refs.commentsFe) {
        return false;
      }

      if (!window.showCommentDialog) {
        const error = {
          debug: 'window.showCommentDialog does not exist',
          message: 'comments.error.not_found',
          statusCode: 404,
        };
        this.$nuxt.error(error);
      }

      window.showCommentDialog(this.commentsContainer, {
        articleId: this.$route.params.id,
        channelId: this.$channelConfig('settings').externalId,
        publisherId: 1,
        articleUrl: this.articleUrl,
        articleTitle: '',
        locale: this.$channelConfig('settings').locale,
        tpl: 'default',
        customTpl: 'lt',
        reverse: false,
        noScroll: true,
        orderBy: 'DATE_DESC',
        offset: 0,
        showAll: false,
        showLogout: true,
        ads: { config: { freq: 5, emitEvent: true } },
        noPreloader: false,
        readOnly: !this.hasChannelAccess,
        moreRepliesLimit: 15,
        useCommentAPIv2: true,
        enableLoginApiClient: false,
        enablePiano: true,
        dateFormat: 'YYYY-MM-DD HH:mm:ss',
        loginAction: () => this.openLoginFormModal(),
      });
    },
    openLoginFormModal() {
      document.getElementById('openLoginModal')?.click();
    },
  },
  head() {
    const meta = getArticleMeta({ $channelConfig: this.$channelConfig, article: this.article, path: this.$route.path });
    meta.url = this.$route.path;
    meta.type = 'website';

    let data = [...meta.data];
    data?.push({ hid: 'cXenseParse:eks-taxonomy', name: 'cXenseParse:eks-taxonomy', content: 'comments' });
    data?.push({ hid: 'cXenseParse:url', name: 'cXenseParse:url', content: `${this.$channelConfig('meta').base.baseUrl}${this.articleUrl}` });

    // Override meta.robots default value injected in Meta.ts with 'noindex'
    data = data.filter((item) => item.hid !== 'robots');
    data?.push({ hid: 'robots', name: 'robots', content: 'noindex' });

    return {
      title: meta.title,
      meta: data,
      style: meta.style,
      link: meta.link,
      __dangerouslyDisableSanitizers: ['style'],
    };
  },
});
